import type { ReactNode } from 'react';
import { cn } from '../utils/cn.js';

export interface CTALink {
  label: string;
  to: string;
  icon?: ReactNode;
  external?: boolean;
}

export interface CTAPairProps {
  primary: CTALink;
  secondary?: CTALink;
  /** Optional tertiary link (rendered as text-only). Discouraged — prefer two. */
  tertiary?: CTALink;
  size?: 'md' | 'lg';
  align?: 'left' | 'center';
  className?: string;
}

/**
 * `<CTAPair>` enforces the two-verb CTA discipline across marketing pages.
 *
 * Standard verbs (from docs/design/system/copy-voice.md):
 *   primary demo path       "Book a 30-min demo"
 *   primary self-serve      "Start a free pilot"
 *   primary student path    "Create free profile"
 *   primary partner path    "Post your first opportunity — free"
 *
 * Tertiary is rendered as a text link, not a button, to discourage overuse.
 *
 * Renders <a> tags (not <Link>) so this primitive stays router-agnostic and
 * can be reused outside a React Router context. Pass `external` to open in a
 * new tab.
 */
export function CTAPair({
  primary,
  secondary,
  tertiary,
  size = 'lg',
  align = 'left',
  className,
}: CTAPairProps) {
  const btnSize = size === 'lg' ? 'btn-lg' : '';
  return (
    <div
      className={cn(
        'flex flex-wrap gap-3',
        align === 'center' && 'justify-center',
        className,
      )}
    >
      <a
        href={primary.to}
        {...(primary.external ? { target: '_blank', rel: 'noopener noreferrer' } : null)}
        className={cn('btn btn-primary', btnSize)}
      >
        {primary.icon}
        {primary.label}
      </a>
      {secondary && (
        <a
          href={secondary.to}
          {...(secondary.external ? { target: '_blank', rel: 'noopener noreferrer' } : null)}
          className={cn('btn btn-secondary', btnSize)}
        >
          {secondary.icon}
          {secondary.label}
        </a>
      )}
      {tertiary && (
        <a
          href={tertiary.to}
          {...(tertiary.external ? { target: '_blank', rel: 'noopener noreferrer' } : null)}
          className="btn btn-ghost self-center"
        >
          {tertiary.icon}
          {tertiary.label}
        </a>
      )}
    </div>
  );
}
